import CircularPercentage from "@/components/controls/CircularPercentage";
import Dropdown from "@/components/controls/Dropdown/Dropdown";
import WidgetCard from "@/components/controls/WidgetCard";
import ComparisonChart, {
  ComparisonChartProps
} from "@/components/graphs/ComparisonChart/ComparisonChart";
import BarChart from "@/components/graphs/WinRate/BarChart";
import LineChart from "@/components/graphs/WinRate/LineChart";
import { Switch } from "@/components/ui/switch";
import { UserData } from "@/entities/users/types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import WinRateDrilldownModal from "@/components/layouts/WinRateDrilldownModal/WinRateDrilldownModal";
import { WinRateBreadcrumbsProvider } from "./WinRateBreadcrumbsContext";
import { useDropdownClosing } from "@/services/hooks/useDropdownClosing";
import { periodsDropdownProps } from "./helpers";
import {
  periodEnumToString,
  periodStringToEnum
} from "@/entities/reporting/helpers";
import {
  ChartDataType,
  SettingsPeriod,
  WinRateDirectReportsResponse
} from "@/entities/reporting/types";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { isEmpty } from "lodash";

interface Props {
  userData?: UserData;
  comparisonChartProps: ComparisonChartProps;
  lineChartData: ChartDataType[];
  barChartData: ChartDataType[];
  period: SettingsPeriod;
  periodSelect: (period: SettingsPeriod) => void;
  isLoading?: boolean;
  isAE: boolean;
  drillDownHidden?: boolean;
  directReportsData?: WinRateDirectReportsResponse;
}

const DEFAULT_METHOD = "toDate";
const GRAPH_HEIGHT = 250;

const WinRate: React.FC<Props> = ({
  userData,
  comparisonChartProps,
  lineChartData,
  barChartData,
  period,
  periodSelect,
  isLoading,
  isAE,
  drillDownHidden = false,
  directReportsData
}) => {
  const { t } = useTranslation();
  const showIndustryValue = useFeatureFlag(FeatureFlags.INDUSTRY_VALUE).enabled;

  const [showComparison, setShowComparison] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(DEFAULT_METHOD);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { isDropdownClosing } = useDropdownClosing();

  const isByMonthSelected = selectedMethod === "byMonth";

  useEffect(() => {
    isByMonthSelected && setShowComparison(false);
  }, [selectedMethod]);

  return (
    <>
      <WidgetCard
        cardHeight="27rem"
        title={t("winRate.title")}
        isLoading={isLoading}
        feedBackButtonIdContext="win-rate"
        infoTooltip={t("winRate.info")}
        isDrillDownHidden={isAE || drillDownHidden}
        drillDownOnClick={() => {
          setIsModalOpen(!isModalOpen);
        }}
      >
        <div className="flex w-full h-full justify-between">
          <div className="flex flex-col gap-6 w-[40rem]">
            <Dropdown
              {...periodsDropdownProps(t)}
              defaultValue={periodEnumToString(period)}
              valueSelected={(value) => periodSelect(periodStringToEnum(value))}
            />
            {!isEmpty(comparisonChartProps.datasets) && (
              <div className="flex h-full w-full pb-8 gap-16">
                <div className="flex">
                  <div className="size-48 self-center">
                    <CircularPercentage
                      className="h-full w-full"
                      value={comparisonChartProps.datasets[0].value} // We know that index 0 is "you"
                      isChartVariant
                    />
                  </div>
                </div>
                <div className="max-h-44 max-w-64 flex-1">
                  <ComparisonChart
                    {...comparisonChartProps}
                    unit="%"
                    userData={userData}
                    hideIndustry={!showIndustryValue}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-5 w-[65rem]">
            <Text
              as="div"
              variant="bodySecondary"
              className="flex gap-6 justify-end"
            >
              <div className="flex gap-2 items-center">
                <span className={isByMonthSelected ? "text-disabled" : ""}>
                  {t("winRate.comparePeers")}
                </span>
                <Switch
                  checked={showComparison}
                  onCheckedChange={() =>
                    !isByMonthSelected && setShowComparison(!showComparison)
                  }
                  disabled={isByMonthSelected}
                />
              </div>
              <div className="flex gap-2 items-center">
                <span>{`${t("winRate.calculate")}:`}</span>
                <Dropdown
                  options={{
                    toDate: t("winRate.methods.toDate"),
                    byMonth: t("winRate.methods.byMonth")
                  }}
                  defaultValue={DEFAULT_METHOD}
                  valueSelected={(value) => {
                    setSelectedMethod(value);
                  }}
                />
              </div>
            </Text>
            <div className="h-full w-[95%]">
              {isByMonthSelected ? (
                <BarChart
                  height={GRAPH_HEIGHT}
                  chartData={barChartData}
                  userData={userData}
                  chartWidgetType="winRate"
                />
              ) : (
                <LineChart
                  height={GRAPH_HEIGHT}
                  chartData={lineChartData}
                  showComparison={showComparison}
                  userData={userData}
                  chartWidgetType="winRate"
                  hideIndustry={!showIndustryValue}
                />
              )}
            </div>
          </div>
        </div>
      </WidgetCard>
      <WinRateBreadcrumbsProvider
        leadUser={userData}
        period={period}
        enableDirectReportsData={isModalOpen}
        initialDirectReportsData={directReportsData}
      >
        <WinRateDrilldownModal
          isOpen={isModalOpen && !isDropdownClosing}
          setIsOpen={setIsModalOpen}
          period={period}
          periodSelect={periodSelect}
        />
      </WinRateBreadcrumbsProvider>
    </>
  );
};

export default WinRate;
